import { DAYSTRINGS } from "src/constants/chat";
import moment from "moment";

/**
 * Given a start date (or date-time) and a "subscription period" as reported by the API, calculate when the
 * subscription's expiry is and return as a Date object.
 * The meaning of the subscription period varies by environment: a number of hours on localhost, days on staging, and
 * months on production.
 * @param {Object} subscription
 * @param {boolean} withGrace
 * @returns {Date}
 */
export const getCompanySubscriptionEnd = (subscription, withGrace) => {
    const date = new Date(subscription.startAt);
    switch (subscription.durationUnit) {
    case "hour":
        date.setHours(date.getHours() + subscription.durationNumber);
        if (withGrace) {
            // Move to start of next hour
            date.setHours(date.getHours() + 1);
            date.setMinutes(0, 0);
        }
        break;
    case "day":
        date.setDate(date.getDate() + subscription.durationNumber);
        if (withGrace) {
            // Move to start of next day
            date.setDate(date.getDate() + 1);
            date.setHours(0, 0, 0);
        }
        break;
    case "month":
        date.setMonth(date.getMonth() + subscription.durationNumber);
        if (withGrace) {
            // Move to start of next day
            date.setDate(date.getDate() + 1);
            date.setHours(0, 0, 0);
        }
        break;
    default:
        throw new Error("Unknown environment");
    }
    return date;
};

/**
 * Given an expiry date (or date-time), return a user-facing string for how long is remaining (e.g. "Expires in 7
 * days"), or "Expired" if it has already passed.
 */
export const getCompanySubscriptionExpiryString = (currentSubscription) => {
    const endDate = getCompanySubscriptionEnd(currentSubscription, false);
    const endMoment = moment(endDate);
    if (endMoment.isBefore(moment())) {
        return "Expired";
    }

    let count;
    switch (currentSubscription.durationUnit) {
    case "hour":
        count = Math.floor(endMoment.diff(moment(), "hour"));
        return count === 1 ? "1 Hour Remaining" : `${count} Hours Remaining`;
    case "day":
        count = Math.floor(endMoment.diff(moment(), "day"));
        return count === 1 ? "1 Day Remaining" : `${count} Days Remaining`;
    case "month":
        count = Math.floor(endMoment.diff(moment(), "month"));
        return count === 1 ? "1 Month Remaining" : `${count} Months Remaining`;
    default:
        return "Unknown Expiry";
    }
};

/**
 * @param {number} durationNumber
 * @param {string} durationUnit
 * @returns {string}
 */
export const paymentSlashSuffix = (durationNumber, durationUnit) => {
    if (durationUnit === "month") {
        if (durationNumber === 1) {
            return "mth";
        }
        if (durationNumber === 12) {
            return "yr";
        }
        return durationNumber + " mths";
    }
    if (durationUnit === "day") {
        if (durationNumber === 1) {
            return "day";
        }
        if (durationNumber === 7) {
            return "wk";
        }
        return durationNumber + " days";
    }
    if (durationNumber === 1) {
        return "hr";
    }
    if (durationNumber === 24) {
        return "day";
    }
    return durationNumber + " hrs";
};

export const renderChatListTimestamp = (dateString) => {
    const date = moment(new Date(dateString));
    const today = moment();
    const yesterday = moment().subtract(1, "days");
    const daysDifference = today.diff(date, "days");
    const datesAreSameForToday = date.isSame(today, "year") && date.isSame(today, "month") && date.isSame(today, "day");
    if (daysDifference <= 7) {
        if (date.isBetween(yesterday.startOf("day"), moment().subtract(1, "days").endOf("day"))) {
            return DAYSTRINGS.YESTERDAY;
        }

        if (datesAreSameForToday) { // if message is sent today
            if (date.isAfter(today.subtract(1, "minutes"))) {
                return "Now";
            }
            const readableTime = date.format("hh:mm");
            return readableTime;
        }
        const dayOfWeekAbbreviation = date.format("ddd");
        return dayOfWeekAbbreviation; // Mon to Sun
    }

    if (daysDifference <= 365) {
        return date.format("DD MMM");
    }

    return date.format("DD MMM YYYY");
};

/**
 * @param {string} dateString 
 * @returns {Object} - An object with 'day(01 Dec 2023/Today/Yesterday)' and 'time(09:30)' properties
 */
export const calculateTimelineMessageDate = (dateString) => {
    const response = { time: "", day: "" };
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const readableTime = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hourCycle: "h12" });

    const isToday = date.toISOString().slice(0, 10) === today.toISOString().slice(0, 10);
    const isYesterday = date.toISOString().slice(0, 10) === yesterday.toISOString().slice(0, 10);
    
    if (isToday || isYesterday) {
        response.time = readableTime;
        response.day = isToday ? DAYSTRINGS.TODAY : DAYSTRINGS.YESTERDAY;

        return response;
    }

    const readableDate = date.toLocaleDateString("en-US", { day: "2-digit", month: "long", year: "numeric" });
    const parts = readableDate.replace(",", "").split(" ");

    response.day = `${parts[1]} ${parts[0]} ${parts[2]}`;
    response.time = readableTime;

    return response;
};

/**
 * Formats a Date object into a string using moment.
 * 
 * - For type "datetime": returns "YYYY-MM-DDTHH:mm" (e.g., "2025-07-29T14:30") - ( used on noticeboard)
 * - For type "date": returns "YYYY-MM-DD" (e.g., "2025-07-29") - (use on account settings)
 * 
 * @param {Date} date - A valid JavaScript Date instance.
 * @param {"datetime" | "date"} [type="datetime"] - Format type to return.
 * @returns {string} Formatted date string in the specified format.
 */

export const formatLocalDateTime = (date, type = "datetime") => {
    if (!moment(date).isValid()) return "";

    if (type === "date") {
        return moment(date).format("YYYY-MM-DD");
    }

    return moment(date).format("YYYY-MM-DDTHH:mm");
};
